import classnames from 'classnames/bind'
import { useRouter } from 'next/router'
import { useCallback } from 'react'
import { useInView } from 'react-intersection-observer'
import { useTranslate } from 'react-polyglot'
import { GlobalGridPreset } from '~/@types/grid-preset'
import { GlobalTextPreset } from '~/@types/text-preset'
import { GlobalTextStyling } from '~/@types/text-styling'
import { ALGOLIA_SHOPIFY_ID_PROPERTY_KEY } from '~/lib/algolia'
import { TRACKING_EVENTS } from '~/lib/constants'

import {
  useAlgoliaQueryContext,
  useAlgoliaSearch,
} from '@unlikelystudio/react-ecommerce-hooks'

import { RichTextBlock } from '~/components/Abstracts/RichText'
import { FacetFilters } from '~/components/PopInFilters'
import BlurredCta from '~/components/UI/BlurredCta'
import CardProduct, { CardProductProps } from '~/components/UI/CardProduct'
import TopBar from '~/components/UI/ResultsGrid/TopBar'

import { useLayoutSwitcherProvider } from '~/providers/LayoutSwitcherProvider'
import { useStyle } from '~/providers/StyleProvider'
import { useTracker } from '~/providers/TrackerProvider'

import useTrackProductCardsList, {
  fromProductCardPropsToProductTrackingData,
} from '~/hooks/tracking/useTrackProductCardsList'

import { getGIDFromBase64Id } from '~/utils/shopify-ids'

import { CTA, FILTER } from '~/data/dictionary'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export interface CardProductAlgolia extends CardProductProps {
  storyID?: number
  category?: string
}

export const RESULTS_GRID_LAYOUT = {
  search: 'search',
  category: 'category',
} as const

export interface ResultsGridProps {
  className?: string
  title?: string
  introduction?: RichTextBlock
  initialFacetFilters?: FacetFilters
  topBarClassName?: string
  layout?: (typeof RESULTS_GRID_LAYOUT)[keyof typeof RESULTS_GRID_LAYOUT]
  noResults?: JSX.Element
}

const IMAGE_SIZES = {
  GRID_0: [{ breakpoint: 'md', ratio: 4 / 12 }, { ratio: 1 }],
  GRID_1: [{ breakpoint: 'md', ratio: 330 / 1440 }, { ratio: 1 / 2 }],
}

function ResultsGrid({
  className,
  title,
  introduction,
  initialFacetFilters,
  topBarClassName,
  layout = RESULTS_GRID_LAYOUT.category,
  noResults,
}: ResultsGridProps) {
  const { pathname, query } = useRouter()
  const currentPage = query?.page ? (+query?.page as number) : 1
  const tracker = useTracker()
  const t = useTranslate()
  const { step: currentStep, setStep: setCurrentStep } =
    useLayoutSwitcherProvider()
  const [inViewRef, inView] = useInView({ initialInView: true })
  const [{ facetFilters, index }] = useAlgoliaQueryContext()
  const {
    data,
    hasNextPage,
    fetchNextPage,
    fetchPreviousPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
    isRefetching,
  } = useAlgoliaSearch<CardProductAlgolia>({
    queryOptions: {
      onSuccess(data) {
        function getFilterTrackingValue(filter: string) {
          const [filter_type, filter_value] = filter.split(':')
          return {
            filter_type,
            filter_value,
          }
        }

        let filtersTrackingValue: {
          filter_type: string
          filter_value: string
        }[] = []
        function getFiltersTrackingValue(filters: typeof facetFilters) {
          if (typeof filters === 'string') {
            filtersTrackingValue = [
              ...filtersTrackingValue,
              getFilterTrackingValue(filters),
            ]
          } else if (filters?.length) {
            return filters.map(getFiltersTrackingValue)
          }
        }
        getFiltersTrackingValue(facetFilters)
        const sortedByPrice = index.includes('price')
        tracker.emit(TRACKING_EVENTS.PRODUCT_FILTER, {
          listId: query.uid,
          filters: filtersTrackingValue,
          products: data?.pages?.[0]?.hits?.map((product) =>
            fromProductCardPropsToProductTrackingData(product, {}),
          ),
          sorts: [
            {
              sort_type: sortedByPrice ? 'price' : 'novelty',
              sort_value: sortedByPrice ? 'ASC' : 'DES',
            },
          ],
        })
      },
    },
  })

  const gridStyle = useStyle({ grid: GlobalGridPreset.BASE_GRID })
  const noResultsStyle = useStyle({
    textPreset: GlobalTextPreset.Title12Primary,
    textStyling: GlobalTextStyling.UpperCase,
  })

  const onClickSwitcher = useCallback((step) => {
    setCurrentStep(step)
  }, [])

  const onClickLoadMore = (e) => {
    e.preventDefault()
    fetchNextPage()
  }

  const results = data?.pages?.reduce((acc, page) => {
    return [...acc, ...(page?.hits ?? [])]
  }, [])

  const [setRef, handleItemTracker] = useTrackProductCardsList(
    title,
    results?.map((result) => ({
      ...result,
      shopifyId: getGIDFromBase64Id(
        result?.[ALGOLIA_SHOPIFY_ID_PROPERTY_KEY],
        'Product',
      ),
    })),
  )

  return (
    <div className={cx(css.ResultsGrid, className)} ref={setRef}>
      <div className={css.top}>
        <TopBar
          className={cx(css.topBar, topBarClassName)}
          title={title}
          introduction={introduction}
          layout={layout}
          initialFacetFilters={initialFacetFilters}
          number={data?.pages?.[0]?.nbHits}
          currentLayoutStep={currentStep}
          onClickLayoutButtonCallback={onClickSwitcher}
          hasScrolled={!inView}
        />
      </div>
      <div ref={inViewRef} className={css.scrollRef} />
      {data?.pages?.[0]?.nbHits === 0 &&
        (noResults ? (
          noResults
        ) : (
          <div className={cx(css.noResults, gridStyle)}>
            <h3 className={cx(css.noResultsTitle, noResultsStyle)}>
              {t(FILTER.NO_RESULT)}
            </h3>
          </div>
        ))}

      {hasPreviousPage && (
        <div className={cx(gridStyle, css.loadPreviousPageButton)}>
          <div className={css.ctaWrapper}>
            <BlurredCta
              className={css.cta}
              onClick={() => fetchPreviousPage()}
              isLoading={isFetchingPreviousPage}>
              {isFetchingPreviousPage ? t(CTA.LOADING) : t(CTA.LOAD_MORE)}
            </BlurredCta>
          </div>
        </div>
      )}

      {data?.pages?.length > 0 && (
        <div
          className={cx(css.ResultGrid, {
            [`step-${currentStep}`]: true,
          })}>
          <div className={cx(gridStyle, css.grid)}>
            {data?.pages
              ?.map((page) => {
                return page.hits.map((product, index) => {
                  return (
                    <CardProduct
                      {...product}
                      isSmall
                      priority={index < 4}
                      key={`result_grid_item_${product.objectID}`}
                      className={cx(css.item, {
                        isItemFullWidth:
                          product.isFullWidth && currentStep === 0,
                      })}
                      isFullWidth={product.isFullWidth && currentStep === 0}
                      sizesFromBreakpoints={
                        IMAGE_SIZES?.[`GRID_${currentStep}`]
                      }
                      handleTracker={(params) => {
                        handleItemTracker({
                          products: [product],
                          event: TRACKING_EVENTS.PRODUCT_CLICK,
                          listPosition: index,
                          ...params,
                        })
                      }}
                    />
                  )
                })
              })
              ?.flat()}
          </div>
        </div>
      )}
      {hasNextPage && (!isRefetching || isFetchingNextPage) && (
        <div key="nextPageContainer" className={gridStyle}>
          <div className={css.ctaWrapper}>
            <BlurredCta
              className={css.cta}
              onClick={onClickLoadMore}
              href={{
                pathname,
                query: {
                  ...query,
                  page: [`${currentPage + 1}`],
                },
              }}
              isLoading={isFetchingNextPage}>
              {isFetchingNextPage ? t(CTA.LOADING) : t(CTA.LOAD_MORE)}
            </BlurredCta>
          </div>
        </div>
      )}
    </div>
  )
}

ResultsGrid.defaultProps = {}

export default ResultsGrid
